import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiCheck, FiArrowRight, FiUsers, FiBriefcase } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const fieldClass =
  'w-full bg-white border border-[#EAF1EC] rounded-xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-[#174C3C]/20 text-lg font-medium';
const labelClass = 'text-sm font-black text-[#17211E] uppercase tracking-widest';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('audience') === 'agency' ? 'agency' : 'business';
  const [audience, setAudience] = useState(requested);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    setAudience(requested);
  }, [requested]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Contact | ManagedSite.net';
    const description = 'Contact Dave at ManagedSite.net. Email contact@managedsite.net.';
    const title = 'Contact | ManagedSite.net';
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);

    const formData = new FormData(e.target);
    const payload = Object.fromEntries(formData.entries());
    payload.audience = audience;

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.ok) {
        throw new Error(result.error || 'The message could not be sent.');
      }

      setSubmitted(true);
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: 'contact',
          event_label: payload.audience || 'unknown',
        });
      }
    } catch (error) {
      setSubmitError(error.message || 'The message could not be sent.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="w-full min-h-[70vh] flex items-center justify-center bg-[#FAF8F3] px-4">
        <div className="max-w-2xl w-full bg-white p-12 md:p-20 rounded-[3rem] border border-[#EAF1EC] shadow-xl text-center">
          <div className="w-20 h-20 bg-[#EAF1EC] text-[#174C3C] rounded-full flex items-center justify-center mx-auto mb-10">
            <SafeIcon icon={FiCheck} className="w-10 h-10" />
          </div>
          <h2 className="text-4xl font-extrabold text-[#17211E] mb-6">You will hear from Dave</h2>
          <p className="text-xl text-[#17211E]/75 font-medium leading-relaxed mb-12">
            Most inquiries get a reply within one business day. If you have the website URL and a short note about what is going on, keep those handy.
          </p>
          <Link to="/" className="home-button">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  const isAgency = audience === 'agency';

  return (
    <div className="w-full">
      <section className="bg-[#FAF8F3] pt-24 pb-16 lg:pt-36 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Contact</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10">
            Tell Us What You Need Help With
          </h1>
          <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed max-w-3xl mx-auto font-medium">
            Email{' '}
            <a href="mailto:contact@managedsite.net" className="font-bold text-[#174C3C] underline decoration-2 underline-offset-4">
              contact@managedsite.net
            </a>
            {' '}or use the form. After onboarding, you get a phone number and support desk.
          </p>
        </div>
      </section>

      <section className="bg-[#FAF8F3] pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button
              type="button"
              onClick={() => setAudience('business')}
              className={`p-10 rounded-3xl border-2 transition-all text-left flex flex-col h-full ${!isAgency ? 'bg-[#174C3C] border-[#174C3C] text-white shadow-xl' : 'bg-white border-[#EAF1EC] text-[#17211E] hover:border-[#174C3C]/30'}`}
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-8 ${!isAgency ? 'bg-white/10 text-[#D7B77A]' : 'bg-[#EAF1EC] text-[#174C3C]'}`}>
                <SafeIcon icon={FiBriefcase} className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-black mb-4">I represent a business</h3>
              <p className={`text-lg font-medium leading-relaxed ${!isAgency ? 'text-white/80' : 'text-[#17211E]/60'}`}>
                Website, email, hosting, and support for your own company.
              </p>
            </button>

            <button
              type="button"
              onClick={() => setAudience('agency')}
              className={`p-10 rounded-3xl border-2 transition-all text-left flex flex-col h-full ${isAgency ? 'bg-[#174C3C] border-[#174C3C] text-white shadow-xl' : 'bg-white border-[#EAF1EC] text-[#17211E] hover:border-[#174C3C]/30'}`}
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-8 ${isAgency ? 'bg-white/10 text-[#D7B77A]' : 'bg-[#EAF1EC] text-[#174C3C]'}`}>
                <SafeIcon icon={FiUsers} className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-black mb-4">I represent an agency</h3>
              <p className={`text-lg font-medium leading-relaxed ${isAgency ? 'text-white/80' : 'text-[#17211E]/60'}`}>
                White-label care and support while you keep the client.
              </p>
            </button>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FAF8F3] rounded-[3rem] p-10 md:p-16 border border-[#EAF1EC] shadow-sm">
            <h2 className="text-3xl font-extrabold text-[#17211E] mb-12">
              {isAgency ? 'Agency inquiry' : 'Business inquiry'}
            </h2>

            <form key={audience} method="POST" onSubmit={handleSubmit} className="space-y-8">
              <input type="hidden" name="audience" value={audience} />
              <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
                <label htmlFor="contact-company-fax">Company fax</label>
                <input id="contact-company-fax" type="text" name="company_fax" tabIndex="-1" autoComplete="off" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className={labelClass}>Full Name *</label>
                  <input type="text" name="name" required className={fieldClass} placeholder="Your name" />
                </div>
                <div className="space-y-3">
                  <label className={labelClass}>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className={fieldClass}
                    placeholder={isAgency ? 'you@agency.com' : 'you@company.com'}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className={labelClass}>{isAgency ? 'Agency name *' : 'Company name *'}</label>
                <input
                  type="text"
                  name={isAgency ? 'agency' : 'company'}
                  required
                  className={fieldClass}
                  placeholder={isAgency ? 'Your agency' : 'Your company'}
                />
              </div>

              <div className="space-y-3">
                <label className={labelClass}>How can we help? *</label>
                <select name="help_type" required className={`${fieldClass} appearance-none`}>
                  <option value="">Select an option</option>
                  <option value="Review">Request a Digital Review</option>
                  <option value="Get Started">Get started with monthly care</option>
                  <option value="Support">Request Technical Support</option>
                  <option value="Server management">Server management</option>
                  <option value="Support retainer">Support retainer</option>
                  {isAgency && <option value="Partnership">Discuss an Agency Partnership</option>}
                  <option value="Management">Website Management</option>
                  <option value="Email">Business Email Support</option>
                  <option value="Hosting">Managed Hosting</option>
                  <option value="Design">Web Design and Development</option>
                  <option value="Consulting">Technology Consulting</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="space-y-3">
                <label className={labelClass}>What is going on? *</label>
                <textarea
                  name="message"
                  required
                  rows="4"
                  className={fieldClass}
                  placeholder="A short note is enough."
                />
              </div>

              <div className="pt-2">
                {submitError && (
                  <p className="mb-6 text-center text-lg font-bold text-[#8A2B2B]">{submitError}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="home-button w-full disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? 'Sending…' : isAgency ? 'Send Agency Inquiry' : 'Send Message'}
                </button>
                <p className="text-center mt-6 text-[#17211E]/50 font-bold italic">
                  Dave usually replies within one business day.{' '}
                  <a href="mailto:contact@managedsite.net" className="text-[#174C3C] underline underline-offset-4">
                    contact@managedsite.net
                  </a>
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#17211E] mb-8">Already working with us?</h2>
          <p className="text-xl text-[#17211E]/75 mb-10 font-medium leading-relaxed">
            Use the phone number and support desk from onboarding. This page is for new inquiries.
          </p>
          <Link to="/support" className="inline-flex items-center text-[#174C3C] font-extrabold hover:text-[#3B7C6D] transition-colors text-xl gap-3">
            Support for current clients <SafeIcon icon={FiArrowRight} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Contact;
